import * as React from 'react';
import { useThumbOverlap, getTrackBackground, Range } from 'react-range';

import { cn, formatMillionBalanceFromString } from '@/utils';

const COLORS = ['#A09EB330', '#3366FF', '#A09EB330'];

type Variant = 'default' | 'numeric';

function ThumbLabel({
  rangeRef,
  values,
  index,
  variant,
}: {
  rangeRef: Range | null;
  values: number[];
  index: number;
  variant?: Variant;
}) {
  const [labelValue, labelStyle] = useThumbOverlap(
    rangeRef,
    values,
    index,
    1,
    ' - ',
    (value: string) =>
      `${variant === 'default' ? '$' : ''}${formatMillionBalanceFromString(
        value
      )}`
  );
  return (
    <div
      className={
        Math.abs(values[0] - values[1]) <= 1700000
          ? ''
          : `__label-slider-${index}`
      }
      data-label={index}
      style={{
        display: 'block',
        position: 'absolute',
        top: '-29px',
        color: '#36F',
        fontWeight: 'bold',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        ...(labelStyle as React.CSSProperties),
      }}
    >
      {labelValue as string}
    </div>
  );
}

interface Props {
  maxInputRangeAmmount: number;
  minInputRangeAmmount: number;
  step: number;
  values: number[];
  disabled?: boolean;
  showThumb?: boolean;
  backgroundColor?: string;
  foregroundColor?: string;
  onChange: (number: number[]) => void;
  variant?: Variant;
}

const InputSlider = (props: Props) => {
  const {
    maxInputRangeAmmount,
    minInputRangeAmmount,
    backgroundColor,
    foregroundColor,
    step,
    values,
    disabled,
    onChange,
    variant,
    showThumb = true,
  } = props;

  const rangeRef: any = React.useRef<Range>();
  const Thumb = ({ props, index }: any) => {
    return (
      <div
        {...props}
        key={props.key}
        className="__slider-handle"
        style={{
          ...props.style,
          height: '22px',
          width: '22px',
          borderRadius: '22px',
          border: '3px solid #ffffff',
          backgroundColor: '#3366FF',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0px 2px 6px #AAA',
        }}
      >
        {showThumb && (
          <ThumbLabel
            index={index}
            rangeRef={rangeRef.current}
            values={values}
            variant={variant}
          />
        )}
      </div>
    );
  };
  const Track = ({ props, children }: any) => (
    <div
      style={{
        ...props.style,
        height: '41px',
        marginTop: '1.85rem',
        display: 'flex',
        width: '100%',
      }}
      onMouseDown={props.onMouseDown}
      onTouchStart={props.onTouchStart}
    >
      <div
        ref={props.ref}
        style={{
          height: '4px',
          width: '100%',
          background: getTrackBackground({
            values,
            colors: backgroundColor
              ? [String(backgroundColor), String(foregroundColor)]
              : COLORS,
            min: minInputRangeAmmount,
            max: maxInputRangeAmmount,
          }),
          alignSelf: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
  return (
    <div className={cn({ 'opacity-30': disabled })}>
      <Range
        ref={rangeRef}
        disabled={disabled}
        max={maxInputRangeAmmount}
        min={minInputRangeAmmount}
        renderThumb={Thumb}
        renderTrack={Track}
        step={step}
        values={values}
        onChange={onChange}
      />
    </div>
  );
};

export default InputSlider;
